import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Star, Loader2, Gift, ShieldCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import LeadMagnetDialog from "@/components/site/LeadMagnetDialog";

const periodLabel = (p) => ({ session: "/ session", month: "/ month", block: "/ block" }[p] || "");

const Membership = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadOpen, setLeadOpen] = useState(false);
  const [myMembership, setMyMembership] = useState(null);  // {membership, tier} or null
  const [busyTierId, setBusyTierId] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/memberships")];
      if (user) reqs.push(api.get("/me/membership", { withCredentials: true }));
      const [tRes, mRes] = await Promise.all(reqs);
      setTiers(tRes.data);
      if (user && mRes) setMyMembership(mRes.data);
      else setMyMembership(null);
    } catch {
      toast.error("Could not load memberships.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    loadAll();
  }, [loadAll]);

  const myTierId = myMembership?.membership?.tier_id;
  const isCancelling = myMembership?.membership?.cancel_at_period_end;

  const enroll = async (tier) => {
    if (authLoading) return;
    if (!user) {
      toast.message("Sign in to enroll.");
      navigate("/login?next=/membership");
      return;
    }
    setBusyTierId(tier.id);
    try {
      await api.post("/me/membership/enroll", { tier_id: tier.id }, { withCredentials: true });
      toast.success(`You're on ${tier.name}!`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not enroll.");
    } finally {
      setBusyTierId("");
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel at the end of the current period? You'll keep access until then.")) return;
    try {
      await api.post("/me/membership/cancel", {}, { withCredentials: true });
      toast.success("Cancel scheduled.");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
    }
  };

  const resume = async () => {
    try {
      await api.post("/me/membership/resume", {}, { withCredentials: true });
      toast.success("Resumed.");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume.");
    }
  };

  return (
    <div className="bg-spike-surface" data-testid="membership-page">
      {/* ===== Hero ===== */}
      <section className="relative pt-16 pb-20 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 stripe-pattern opacity-50" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-spike-blue/10 rounded-full blur-3xl" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <span className="inline-flex items-center gap-3 font-orbitron text-[0.7rem] font-bold uppercase tracking-[0.4em] text-spike-blue-light">
            <span className="w-8 h-px bg-spike-blue" /> Memberships <span className="w-8 h-px bg-spike-blue" />
          </span>
          <h1 className="mt-6 font-orbitron font-black uppercase text-4xl sm:text-5xl lg:text-6xl text-spike-text leading-[1] tracking-tight">
            Train like a pro. <br /> <span className="text-spike-blue-light">Join the club.</span>
          </h1>
          <p className="mt-6 font-exo text-spike-text-muted max-w-2xl mx-auto">
            Four plans built for every level — from weekend hitters to team
            captains. No contracts. Cancel any time before the next billing cycle.
          </p>

          {!myMembership?.membership && (
            <button
              type="button"
              onClick={() => setLeadOpen(true)}
              data-testid="hero-lead-magnet-btn"
              className="mt-8 inline-flex items-center gap-3 bg-transparent text-spike-text border border-spike-blue/60 hover:bg-spike-blue/10 font-orbitron font-bold uppercase tracking-[0.22em] text-xs px-6 py-4 transition-colors"
            >
              <Gift className="w-4 h-4 text-spike-blue-light" /> Try a session — free
            </button>
          )}

          {myMembership?.membership && (
            <div
              data-testid="current-plan-banner"
              className="mt-10 inline-flex items-center gap-3 bg-spike-blue/10 border border-spike-blue/40 px-5 py-3 font-orbitron text-[0.7rem] uppercase tracking-[0.3em] text-spike-blue-light"
            >
              <ShieldCheck className="w-4 h-4" />
              You're on <strong className="text-spike-text">{myMembership.tier?.name}</strong>
              {isCancelling && <span className="text-amber-300">· cancelling soon</span>}
            </div>
          )}
        </div>
      </section>

      {/* ===== Pricing Tiers ===== */}
      <section className="relative py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex justify-center items-center py-20 text-spike-text-muted gap-3 font-exo">
              <Loader2 className="w-5 h-5 animate-spin text-spike-blue-light" /> Loading plans...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5" data-testid="pricing-grid">
              {tiers.map((t, i) => {
                const isCurrent = myTierId === t.id;
                const busy = busyTierId === t.id;
                return (
                  <motion.div
                    key={t.id}
                    data-testid={`tier-${t.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className={`relative bg-spike-elevated p-8 lg:p-10 flex flex-col ${
                      isCurrent ? "ring-2 ring-spike-blue z-10 bg-gradient-to-b from-spike-blue/20 to-black"
                        : t.highlight ? "ring-2 ring-spike-blue/40 z-10 bg-gradient-to-b from-spike-blue/5 to-black" : ""
                    }`}
                  >
                    {isCurrent ? (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-spike-blue text-black text-[0.6rem] font-orbitron font-bold uppercase tracking-[0.3em] px-4 py-1 flex items-center gap-1" data-testid={`tier-${t.id}-current-badge`}>
                        <ShieldCheck className="w-3 h-3" strokeWidth={2.5} /> Your plan
                      </div>
                    ) : t.highlight ? (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-spike-blue text-black text-[0.6rem] font-orbitron font-bold uppercase tracking-[0.3em] px-4 py-1 flex items-center gap-1">
                        <Star className="w-3 h-3" strokeWidth={2.5} /> Most popular
                      </div>
                    ) : null}

                    <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.35em] text-spike-blue-light">
                      0{i + 1} · {t.tagline}
                    </div>
                    <h3 className="mt-3 font-orbitron font-black uppercase text-2xl text-spike-text tracking-tight">
                      {t.name}
                    </h3>

                    <div className="mt-6 flex items-baseline gap-2">
                      <span className="font-orbitron font-black text-5xl text-spike-text">
                        ${t.price_cad}
                      </span>
                      <span className="text-spike-text-muted font-exo text-sm">{periodLabel(t.period)}</span>
                    </div>
                    <div className="mt-1 font-orbitron text-[0.6rem] uppercase tracking-[0.3em] text-spike-dim">CAD</div>

                    {(t.discount_percent > 0 || t.monthly_free_hours > 0) && (
                      <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`tier-${t.id}-perks`}>
                        {t.discount_percent > 0 && (
                          <span className="inline-flex items-center gap-1 bg-spike-blue/10 border border-spike-blue/40 text-spike-blue-light font-orbitron text-[0.6rem] uppercase tracking-[0.2em] px-2 py-1">
                            {t.discount_percent}% off lanes
                          </span>
                        )}
                        {t.monthly_free_hours > 0 && (
                          <span className="inline-flex items-center gap-1 bg-spike-blue/10 border border-spike-blue/40 text-spike-blue-light font-orbitron text-[0.6rem] uppercase tracking-[0.2em] px-2 py-1">
                            {t.monthly_free_hours} free hrs / mo
                          </span>
                        )}
                      </div>
                    )}

                    <ul className="mt-6 space-y-3 flex-1">
                      {t.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 font-exo text-sm text-spike-text">
                          <Check className="w-4 h-4 text-spike-blue-light mt-0.5 shrink-0" strokeWidth={2.5} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA logic — depends on current plan state */}
                    {isCurrent ? (
                      isCancelling ? (
                        <button
                          type="button"
                          data-testid={`tier-${t.id}-resume`}
                          onClick={resume}
                          className="mt-10 inline-flex items-center justify-center gap-3 bg-spike-blue text-black hover:bg-spike-blue-hover font-orbitron font-bold uppercase tracking-[0.22em] text-xs px-6 py-4 transition-colors"
                        >
                          Resume plan <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          data-testid={`tier-${t.id}-cancel`}
                          onClick={cancel}
                          className="mt-10 inline-flex items-center justify-center gap-3 bg-transparent text-spike-text border border-red-400/50 hover:border-red-400 hover:bg-red-500/10 font-orbitron font-bold uppercase tracking-[0.22em] text-xs px-6 py-4 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        data-testid={`tier-${t.id}-enroll`}
                        onClick={() => enroll(t)}
                        disabled={busy}
                        className={`mt-10 inline-flex items-center justify-center gap-3 font-orbitron font-bold uppercase tracking-[0.22em] text-xs px-6 py-4 transition-colors disabled:opacity-60 ${
                          t.highlight
                            ? "bg-spike-blue text-black hover:bg-spike-blue-hover"
                            : "bg-transparent text-spike-text border border-white/10 hover:border-spike-blue hover:bg-spike-blue/10"
                        }`}
                      >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                          {myMembership?.membership ? "Switch to this" : (user ? "Choose plan" : "Sign in to enroll")}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ===== Compare callout ===== */}
      <section className="relative py-20 lg:py-28 bg-spike-surface border-y border-white/10" data-testid="comparison-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <span className="font-orbitron text-[0.7rem] uppercase tracking-[0.4em] text-spike-blue-light">All plans include</span>
              <h2 className="mt-3 font-orbitron font-black uppercase text-3xl sm:text-4xl text-spike-text leading-tight">
                What's always <br />
                <span className="text-spike-blue-light">on the house.</span>
              </h2>
            </div>
            <p className="font-exo text-spike-text-muted max-w-md">
              Lounge access, complimentary Wi-Fi, on-site parking, and a friendly
              squad behind the desk — every visit, every plan.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
            {[
              { title: "Lounge", body: "Comfortable seating, water station, viewing area between sessions." },
              { title: "Wi-Fi & parking", body: "Free guest Wi-Fi and on-site parking for the whole crew." },
              { title: "Pro-grade gear", body: "Rent professional bats, pads, gloves — or bring your own kit." },
            ].map((item) => (
              <div key={item.title} className="bg-spike-elevated p-8" data-testid={`include-card-${item.title}`}>
                <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.35em] text-spike-blue-light">Included</div>
                <h3 className="mt-2 font-orbitron font-bold uppercase text-xl text-spike-text">{item.title}</h3>
                <p className="mt-3 font-exo text-sm text-spike-text-muted leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ teaser ===== */}
      <section className="relative py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="font-orbitron text-[0.7rem] uppercase tracking-[0.4em] text-spike-blue-light">FAQ</span>
            <h2 className="mt-3 font-orbitron font-black uppercase text-3xl sm:text-4xl text-spike-text leading-tight">
              Common questions <br /> from new members.
            </h2>
            <p className="mt-6 font-exo text-spike-text-muted max-w-md">
              Still unsure? Call us at (905) 678-9725 — we'd rather have a quick
              conversation than sell you the wrong plan.
            </p>
            <Link
              to="/book"
              data-testid="faq-cta-book"
              className="mt-8 inline-flex items-center gap-3 bg-spike-blue hover:bg-spike-blue-hover text-black font-orbitron font-bold uppercase tracking-[0.22em] text-sm px-8 py-5 transition-colors"
            >
              Try a session <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="space-y-px bg-white/5" data-testid="faq-list">
            <Accordion type="single" collapsible className="w-full">
              {[
                { q: "Can I cancel anytime?", a: "Yes. Memberships are billed monthly. Cancel before the next cycle — no fees, no hassle." },
                { q: "Do I need to bring my own equipment?", a: "No. We rent professional bats, pads, gloves, and helmets at the venue. Pro Player includes rentals." },
                { q: "Is the bowling machine cricket or baseball?", a: "Both. Bola Professional handles cricket variations; Iron Mike is built for baseball pitching." },
                { q: "Can teams book multiple lanes together?", a: "Absolutely — that's exactly what Team Block is for. Up to 4 lanes per 90-minute slot." },
              ].map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="bg-spike-elevated border border-white/0 data-[state=open]:bg-spike-elevated data-[state=open]:border-spike-blue/30 hover:border-spike-blue/30 transition-all"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="px-6 py-5 font-orbitron font-bold uppercase tracking-wider text-spike-text text-sm hover:no-underline"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-content-${i}`}
                    className="px-6 pb-6 font-exo text-spike-text-muted text-sm leading-relaxed"
                  >
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <LeadMagnetDialog open={leadOpen} onOpenChange={setLeadOpen} source="free_session" />
    </div>
  );
};

export default Membership;
